import { ApplicationConfig, provideBrowserGlobalErrorListeners, provideZonelessChangeDetection } from '@angular/core';
import { HttpClient, provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { provideRouter, withComponentInputBinding, withRouterConfig } from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import { CORE_INTERCEPTORS, provideCore } from '@corelabs/angular/core';
import { provideAccount } from '@corelabs/angular/account';
import { provideMessages } from '@corelabs/angular/messages';
import { provideClI18n } from '@corelabs/angular/ui';
import { provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { provideEffects } from '@ngrx/effects';

import { environment } from '@env/environment';

import { apiErrorMessageInterceptor, apiLanguageInterceptor, HttpService } from './core/http';
import { appRoutes } from './app.routing';
import { MissingTranslationHandler, provideTranslateService, TranslateLoader } from '@ngx-translate/core';
import { clI18nConfig, customMissingTranslationHandler, translateLoaderStatic } from './i18n';
import { provideProfileStore } from './store/profile';
import { provideSessionStore } from './store/session';

export const appConfig: ApplicationConfig = {
    providers: [
        provideBrowserGlobalErrorListeners(),
        provideZonelessChangeDetection(),
        provideRouter(appRoutes, withComponentInputBinding(), withRouterConfig({ onSameUrlNavigation: 'reload' })),
        provideServiceWorker('ngsw-worker.js', {
            enabled: environment.sw,
            registrationStrategy: 'registerWhenStable:30000',
        }),
        provideHttpClient(withFetch(), withInterceptors([...CORE_INTERCEPTORS, apiLanguageInterceptor, apiErrorMessageInterceptor])),
        provideStore(),
        // A `define` literal, not environment.production — only a literal folds away, dropping the instrumentation.
        ...(NGRX_DEVTOOLS ? [provideStoreDevtools({ maxAge: 25, connectInZone: true })] : []),
        provideEffects(),
        provideCore({ serverUrl: environment.serverUrl + '/api/' }),
        // After provideCore() so the app's HttpService (corelabs client + app fluent methods) wins the HttpClient token.
        {
            provide: HttpClient,
            useClass: HttpService,
        },
        provideAccount({ skip: ['logout-error'] }),
        provideMessages({ secondsShown: 5, visibleCount: 2, updateInterval: 100 }),
        provideClI18n(clI18nConfig),
        ...provideSessionStore(),
        // App-global like `session`: its bridge has to be listening when the first session response lands, before the shell route exists.
        ...provideProfileStore(),
        provideTranslateService({
            loader: { provide: TranslateLoader, useFactory: translateLoaderStatic },
            missingTranslationHandler: { provide: MissingTranslationHandler, useFactory: customMissingTranslationHandler },
        }),
    ],
};
